/**
 * 左侧目录条（Substack / godepth 阅读页同款）—— 每个页面都有。
 *
 * 目录的单位是「当前所在的段」（盘前 / 盘中 / 复盘 / 研究 / 系统）：
 *   - 一条横线 = 段内一个页面，当前页最长最深；
 *   - 当前页若登记了小节（<Section> / useViews），小节以更短的缩进横线挂在它下面。
 * 指针移入或键盘 Tab 进入时，目录卡片从横线处展开 —— 卡片就是这个容器自己
 * 的一部分、原点与横线重合，鼠标从横线移向卡片不会跨过任何空隙。
 */
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { NAV_ROUTES, SEGMENT_LABEL, matchRoute, type Segment } from './registry';
import { useSectionStore, type SectionKind, type SectionMeta } from './sections-store';

export interface StripPage {
  path: string;
  name: string;
}

export function SectionStripView({
  segment,
  pages,
  currentPath,
  sections,
  active,
  onJump,
}: {
  segment: Segment | undefined;
  pages: StripPage[];
  currentPath: string | undefined;
  sections: SectionMeta[];
  active: string | null;
  onJump: (id: string, kind: SectionKind) => void;
}) {
  if (!segment || pages.length === 0) return null;
  return (
    <div className="group fixed left-4 top-1/2 z-40 hidden -translate-y-1/2 lg:block">
      {/* 静止态：横线 */}
      <div
        aria-hidden
        className="flex flex-col gap-2.5 py-3 pl-1 pr-4 transition-opacity group-focus-within:opacity-0 group-hover:opacity-0"
      >
        {pages.map((p) => {
          const on = p.path === currentPath;
          return (
            <div key={p.path} className="flex flex-col gap-2">
              <span
                className={cn(
                  'block h-[2px] rounded-full',
                  on ? 'w-6 bg-slate-700 dark:bg-slate-200' : 'w-4 bg-slate-300 dark:bg-slate-600',
                )}
              />
              {on &&
                sections.map((s) => (
                  <span
                    key={s.id}
                    className={cn(
                      'ml-2 block h-[2px] rounded-full',
                      s.id === active ? 'w-3.5 bg-sky-500' : 'w-2.5 bg-slate-300/80 dark:bg-slate-600/80',
                    )}
                  />
                ))}
            </div>
          );
        })}
      </div>

      {/* 展开态：目录卡片，与横线同原点 */}
      <nav
        aria-label={`${SEGMENT_LABEL[segment]}目录`}
        className={cn(
          'absolute left-0 top-1/2 max-h-[80vh] w-64 -translate-y-1/2 overflow-y-auto rounded-2xl border border-border bg-card/95 p-3 shadow-float backdrop-blur-xl',
          'pointer-events-none opacity-0 transition-opacity duration-150',
          'group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100',
        )}
      >
        <div className="flex items-baseline justify-between px-2 pb-1.5">
          <span className="text-xs font-semibold text-foreground">目录 · {SEGMENT_LABEL[segment]}</span>
          <span className="text-[11px] text-muted-foreground">{pages.length} 页</span>
        </div>
        <ul>
          {pages.map((p) => {
            const on = p.path === currentPath;
            return (
              <li key={p.path}>
                <Link
                  to={p.path}
                  aria-current={on ? 'page' : undefined}
                  className={cn(
                    'block truncate rounded-lg px-2 py-1.5 text-sm transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                    on ? 'bg-accent font-semibold text-accent-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {p.name}
                </Link>
                {on && sections.length > 0 && (
                  <ul className="my-1 ml-3 border-l border-border pl-2">
                    {sections.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => onJump(s.id, s.kind)}
                          aria-current={s.id === active ? 'true' : undefined}
                          className={cn(
                            'w-full truncate rounded-md px-2 py-1 text-left text-[13px] transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                            s.id === active ? 'font-medium text-sky-700 dark:text-sky-300' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                          )}
                        >
                          {s.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}

export function SectionStrip() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const sections = useSectionStore((s) => s.sections);
  const active = useSectionStore((s) => s.active);
  const current = matchRoute(pathname);
  const pages = current?.segment ? NAV_ROUTES.filter((r) => r.segment === current.segment) : [];
  const jump = (id: string, kind: SectionKind) => {
    if (kind === 'view') {
      const sp = new URLSearchParams(window.location.search);
      sp.set('v', id);
      navigate({ search: sp.toString() }, { replace: true });
    } else {
      document.getElementById(`sec-${id}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };
  return (
    <SectionStripView
      segment={current?.segment}
      pages={pages}
      currentPath={current?.path}
      sections={sections}
      active={active}
      onJump={jump}
    />
  );
}
